// ─────────────────────────────────────────────
//  Cascade AI — First-Run Setup Wizard (Ink TUI)
// ─────────────────────────────────────────────
//
//  Flow: PROVIDER_SELECT → API_KEYS → FETCH_MODELS → TIER_ASSIGN → SAVE
//
//  Supports multiple Azure deployments and multiple OpenAI-compatible
//  endpoints — each appears as a separate selectable entry in tier assignment.

import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { Box, Text, useApp, useInput, render } from 'ink';
import SelectInput from 'ink-select-input';
import Spinner from 'ink-spinner';
import path from 'node:path';
import fs from 'node:fs/promises';
import { randomUUID } from 'node:crypto';
import { CascadeConfigSchema } from '../../config/schema.js';
import { CASCADE_CONFIG_FILE, GLOBAL_CONFIG_DIR } from '../../constants.js';
import type { CascadeConfig } from '../../types.js';
import { SafeTextInput } from '../components/SafeTextInput.js';
import { getTheme } from '../themes/index.js';
import { Frame, FieldBox, TierCard } from './components.js';

// ── Types ─────────────────────────────────────

type ProviderType = 'anthropic' | 'openai' | 'gemini' | 'azure' | 'ollama' | 'openai-compatible';

interface ProviderEntry {
  id: string;
  type: ProviderType;
  label: string;          // display name
  apiKey?: string;
  baseUrl?: string;
  deploymentName?: string;
  apiVersion?: string;
}

interface FetchedModel {
  id: string;
  name: string;
  providerLabel: string;
}

type WizardStep =
  | 'PROVIDER_SELECT'
  | 'API_KEYS'
  | 'FETCH_MODELS'
  | 'TIER_ASSIGN'
  | 'SAVE'
  | 'DONE';

interface WizardState {
  step: WizardStep;
  selectedTypes: Set<ProviderType>;
  entries: ProviderEntry[];          // one per configured provider/deployment
  currentEntryIdx: number;           // which entry we are collecting keys for
  addingAnotherAzure: boolean;
  addingAnotherCompat: boolean;
  addingAnotherOllama: boolean;
  fetchedModels: FetchedModel[];
  fetchLog: string[];
  tierT1: string;                    // 'auto' or model id
  tierT2: string;
  tierT3: string;
  tierSelectFocus: 'T1' | 'T2' | 'T3';
  error: string | null;
}

type WizardAction =
  | { type: 'TOGGLE_PROVIDER'; provider: ProviderType }
  | { type: 'TOGGLE_ALL' }
  | { type: 'INVERT_SELECTION' }
  | { type: 'CONFIRM_PROVIDERS' }
  | { type: 'SET_ENTRY_FIELD'; field: keyof ProviderEntry; value: string }
  | { type: 'NEXT_ENTRY' }
  | { type: 'ADD_AZURE' }
  | { type: 'ADD_COMPAT' }
  | { type: 'ADD_OLLAMA' }
  | { type: 'SKIP_MORE' }
  | { type: 'SET_FETCH_LOG'; line: string }
  | { type: 'SET_MODELS'; models: FetchedModel[] }
  | { type: 'GO_FETCH' }
  | { type: 'SET_TIER'; tier: 'T1' | 'T2' | 'T3'; value: string }
  | { type: 'SET_TIER_FOCUS'; tier: 'T1' | 'T2' | 'T3' }
  | { type: 'GO_SAVE' }
  | { type: 'GO_DONE' }
  | { type: 'SET_ERROR'; error: string };

const PROVIDER_LABELS: Record<ProviderType, string> = {
  anthropic: 'Anthropic Claude',
  openai: 'OpenAI GPT',
  gemini: 'Google Gemini',
  azure: 'Azure OpenAI',
  ollama: 'Ollama (local)',
  'openai-compatible': 'OpenAI-Compatible',
};

const providerOrder: ProviderType[] = ['anthropic', 'openai', 'gemini', 'azure', 'ollama', 'openai-compatible'];

function buildInitialEntries(types: Set<ProviderType>): ProviderEntry[] {
  return [...types].map(type => ({
    id: randomUUID(),
    type,
    label: type === 'azure' ? 'Azure deployment 1'
      : type === 'openai-compatible' ? 'Custom endpoint 1'
      : PROVIDER_LABELS[type],
  }));
}

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'TOGGLE_PROVIDER': {
      const next = new Set(state.selectedTypes);
      if (next.has(action.provider)) next.delete(action.provider);
      else next.add(action.provider);
      return { ...state, selectedTypes: next };
    }
    case 'TOGGLE_ALL': {
      const allSelected = state.selectedTypes.size === providerOrder.length;
      return { ...state, selectedTypes: allSelected ? new Set() : new Set(providerOrder) };
    }
    case 'INVERT_SELECTION': {
      const next = new Set<ProviderType>();
      providerOrder.forEach(p => {
        if (!state.selectedTypes.has(p)) next.add(p);
      });
      return { ...state, selectedTypes: next };
    }
    case 'CONFIRM_PROVIDERS': {
      const entries = buildInitialEntries(state.selectedTypes);
      return { ...state, entries, currentEntryIdx: 0, step: 'API_KEYS' };
    }
    case 'SET_ENTRY_FIELD': {
      const updated = [...state.entries];
      updated[state.currentEntryIdx] = {
        ...updated[state.currentEntryIdx]!,
        [action.field]: action.value,
      };
      return { ...state, entries: updated };
    }
    case 'NEXT_ENTRY': {
      const next = state.currentEntryIdx + 1;
      if (next >= state.entries.length) {
        return { ...state, step: 'FETCH_MODELS', currentEntryIdx: 0 };
      }
      return { ...state, currentEntryIdx: next };
    }
    case 'ADD_AZURE': {
      const prevAzure = state.entries.find(e => e.type === 'azure');
      const newEntry: ProviderEntry = {
        id: randomUUID(),
        type: 'azure',
        label: `Azure deployment ${state.entries.filter(e => e.type === 'azure').length + 1}`,
        baseUrl: prevAzure?.baseUrl,
        apiKey: prevAzure?.apiKey,
        apiVersion: prevAzure?.apiVersion,
      };
      return {
        ...state,
        entries: [...state.entries, newEntry],
        currentEntryIdx: state.entries.length,
        addingAnotherAzure: false,
      };
    }
    case 'ADD_COMPAT': {
      const newEntry: ProviderEntry = {
        id: randomUUID(),
        type: 'openai-compatible',
        label: `Custom endpoint ${state.entries.filter(e => e.type === 'openai-compatible').length + 1}`,
      };
      return {
        ...state,
        entries: [...state.entries, newEntry],
        currentEntryIdx: state.entries.length,
        addingAnotherCompat: false,
      };
    }
    case 'ADD_OLLAMA': {
      const newEntry: ProviderEntry = {
        id: randomUUID(),
        type: 'ollama',
        label: `Ollama endpoint ${state.entries.filter(e => e.type === 'ollama').length + 1}`,
      };
      return {
        ...state,
        entries: [...state.entries, newEntry],
        currentEntryIdx: state.entries.length,
        addingAnotherOllama: false,
      };
    }
    case 'SKIP_MORE':
      return { ...state, addingAnotherAzure: false, addingAnotherCompat: false, addingAnotherOllama: false, step: 'FETCH_MODELS', currentEntryIdx: 0 };
    case 'GO_FETCH':
      return { ...state, step: 'FETCH_MODELS', fetchLog: [], fetchedModels: [] };
    case 'SET_FETCH_LOG':
      return { ...state, fetchLog: [...state.fetchLog, action.line] };
    case 'SET_MODELS':
      return { ...state, fetchedModels: action.models, step: 'TIER_ASSIGN' };
    case 'SET_TIER':
      return {
        ...state,
        tierT1: action.tier === 'T1' ? action.value : state.tierT1,
        tierT2: action.tier === 'T2' ? action.value : state.tierT2,
        tierT3: action.tier === 'T3' ? action.value : state.tierT3,
      };
    case 'SET_TIER_FOCUS':
      return { ...state, tierSelectFocus: action.tier };
    case 'GO_SAVE':
      return { ...state, step: 'SAVE' };
    case 'GO_DONE':
      return { ...state, step: 'DONE' };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    default:
      return state;
  }
}

// ── Main Wizard Component ─────────────────────

interface SetupWizardProps {
  workspacePath: string;
  onComplete: (config: CascadeConfig) => void;
}

export function SetupWizard({ workspacePath, onComplete }: SetupWizardProps): React.ReactElement {
  const { exit } = useApp();
  const theme = getTheme('cascade');
  const savedConfigRef = useRef<CascadeConfig | null>(null);

  const [state, dispatch] = useReducer(wizardReducer, {
    step: 'PROVIDER_SELECT',
    selectedTypes: new Set<ProviderType>(),
    entries: [],
    currentEntryIdx: 0,
    addingAnotherAzure: false,
    addingAnotherCompat: false,
    addingAnotherOllama: false,
    fetchedModels: [],
    fetchLog: [],
    tierT1: 'auto',
    tierT2: 'auto',
    tierT3: 'auto',
    tierSelectFocus: 'T1',
    error: null,
  });

  const [providerCursor, setProviderCursor] = useState(0);
  const [fieldBuffer, setFieldBuffer] = useState('');
  const [fieldStage, setFieldStage] = useState<'apiKey' | 'baseUrl' | 'deploymentName' | 'label' | 'apiVersion' | 'askMore'>('apiKey');
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  // ── Step: FETCH_MODELS ───────────────────────
  useEffect(() => {
    if (state.step !== 'FETCH_MODELS') return;

    const run = async () => {
      const models: FetchedModel[] = [];
      // Always include "auto"
      for (const entry of state.entries) {
        try {
          dispatchRef.current({ type: 'SET_FETCH_LOG', line: `  ⠋ ${entry.label} — connecting...` });
          // Build a minimal provider config to call listModels
          const { type, apiKey, baseUrl, deploymentName } = entry;
          if (type === 'ollama') {
            const { OllamaProvider } = await import('../../providers/ollama.js');
            const dummyModel = { id: 'dummy', name: 'dummy', provider: type as never, contextWindow: 0, isVisionCapable: false, inputCostPer1kTokens: 0, outputCostPer1kTokens: 0, maxOutputTokens: 0, supportsStreaming: false, isLocal: true };
            const p = new OllamaProvider({ type, baseUrl }, dummyModel);
            const fetched = await p.listModels();
            fetched.forEach(m => models.push({ id: m.id, name: m.name, providerLabel: entry.label }));
            dispatchRef.current({ type: 'SET_FETCH_LOG', line: `  ✔ ${entry.label} — ${fetched.length} models` });
          } else if (type === 'anthropic') {
            const { AnthropicProvider } = await import('../../providers/anthropic.js');
            const dummyModel = { id: 'dummy', name: 'dummy', provider: type as never, contextWindow: 0, isVisionCapable: false, inputCostPer1kTokens: 0, outputCostPer1kTokens: 0, maxOutputTokens: 0, supportsStreaming: false, isLocal: false };
            const p = new AnthropicProvider({ type, apiKey }, dummyModel);
            const fetched = await p.listModels();
            fetched.forEach(m => models.push({ id: m.id, name: m.name, providerLabel: entry.label }));
            dispatchRef.current({ type: 'SET_FETCH_LOG', line: `  ✔ ${entry.label} — ${fetched.length} models` });
          } else if (type === 'openai' || type === 'openai-compatible') {
            const { OpenAIProvider } = await import('../../providers/openai.js');
            const { OpenAICompatibleProvider } = await import('../../providers/openai-compatible.js');
            const dummyModel = { id: 'dummy', name: 'dummy', provider: type as never, contextWindow: 0, isVisionCapable: false, inputCostPer1kTokens: 0, outputCostPer1kTokens: 0, maxOutputTokens: 0, supportsStreaming: false, isLocal: false };
            const cfg = { type, apiKey, ...(baseUrl ? { baseUrl } : {}) };
            const p = type === 'openai' ? new OpenAIProvider(cfg as never, dummyModel) : new OpenAICompatibleProvider(cfg as never, dummyModel);
            const fetched = await p.listModels();
            fetched.forEach(m => models.push({ id: m.id, name: m.name, providerLabel: entry.label }));
            dispatchRef.current({ type: 'SET_FETCH_LOG', line: `  ✔ ${entry.label} — ${fetched.length} models` });
          } else if (type === 'gemini') {
            const { GeminiProvider } = await import('../../providers/gemini.js');
            const dummyModel = { id: 'dummy', name: 'dummy', provider: type as never, contextWindow: 0, isVisionCapable: false, inputCostPer1kTokens: 0, outputCostPer1kTokens: 0, maxOutputTokens: 0, supportsStreaming: false, isLocal: false };
            const p = new GeminiProvider({ type, apiKey }, dummyModel);
            const fetched = await p.listModels();
            fetched.forEach(m => models.push({ id: m.id, name: m.name, providerLabel: entry.label }));
            dispatchRef.current({ type: 'SET_FETCH_LOG', line: `  ✔ ${entry.label} — ${fetched.length} models` });
          } else if (type === 'azure') {
            const { AzureOpenAIProvider } = await import('../../providers/azure.js');
            const actualModelId = deploymentName || `azure-${entry.id}`;
            const dummyModel = { id: actualModelId, name: actualModelId, provider: type as never, contextWindow: 0, isVisionCapable: false, inputCostPer1kTokens: 0, outputCostPer1kTokens: 0, maxOutputTokens: 0, supportsStreaming: false, isLocal: false };
            const p = new AzureOpenAIProvider({ type, apiKey, baseUrl, deploymentName, apiVersion: entry.apiVersion }, dummyModel);
            const fetched = await p.listModels();
            fetched.forEach(m => models.push({ id: m.id, name: m.name, providerLabel: entry.label }));
            dispatchRef.current({ type: 'SET_FETCH_LOG', line: `  ✔ ${entry.label} — ${fetched.length} models` });
          }
        } catch (err) {
          dispatchRef.current({ type: 'SET_FETCH_LOG', line: `  ✘ ${entry.label} — ${err instanceof Error ? err.message : 'failed'}` });
        }
      }
      dispatchRef.current({ type: 'SET_MODELS', models });
    };

    run();
  }, [state.step, state.entries]);

  // ── Step: SAVE ───────────────────────────────
  useEffect(() => {
    if (state.step !== 'SAVE') return;

    const run = async () => {
      try {
        const providers = state.entries.map(e => ({
          type: e.type,
          ...(e.apiKey ? { apiKey: e.apiKey } : {}),
          ...(e.baseUrl ? { baseUrl: e.baseUrl } : {}),
          ...(e.deploymentName ? { deploymentName: e.deploymentName } : {}),
          ...(e.apiVersion ? { apiVersion: e.apiVersion } : {}),
        }));

        const models: Record<string, string> = {};
        if (state.tierT1 !== 'auto') models['t1'] = state.tierT1;
        if (state.tierT2 !== 'auto') models['t2'] = state.tierT2;
        if (state.tierT3 !== 'auto') models['t3'] = state.tierT3;

        // Any tier left on "Auto" turns on Cascade Auto so the task analyzer
        // routes each subtask to the benchmark-best model for its type. Without
        // this flag "Auto" silently fell back to the static priority list.
        const anyAuto = state.tierT1 === 'auto' || state.tierT2 === 'auto' || state.tierT3 === 'auto';

        const rawConfig = {
          providers,
          ...(Object.keys(models).length ? { models } : {}),
          ...(anyAuto ? { cascadeAuto: true } : {}),
        };
        const config = CascadeConfigSchema.parse(rawConfig);

        const configDir = path.join(workspacePath, '.cascade');
        await fs.mkdir(configDir, { recursive: true });
        const configPath = path.join(workspacePath, CASCADE_CONFIG_FILE);
        await fs.writeFile(configPath, JSON.stringify(config, null, 2), 'utf-8');

        savedConfigRef.current = config as CascadeConfig;
        dispatchRef.current({ type: 'GO_DONE' });
      } catch (err) {
        dispatchRef.current({ type: 'SET_ERROR', error: err instanceof Error ? err.message : String(err) });
      }
    };

    run();
  }, [state.step, state.entries, state.tierT1, state.tierT2, state.tierT3, workspacePath, onComplete, exit]);

  // ── Input handling ────────────────────────────
  useInput((_input, key) => {
    if (state.step === 'DONE') {
      if (key.return || key.escape) {
        if (savedConfigRef.current) onComplete(savedConfigRef.current);
        exit();
      }
      return;
    }
    if (state.step === 'PROVIDER_SELECT') {
      if (key.upArrow) setProviderCursor(p => (p <= 0 ? providerOrder.length - 1 : p - 1));
      if (key.downArrow) setProviderCursor(p => (p >= providerOrder.length - 1 ? 0 : p + 1));
      if (_input === ' ') dispatch({ type: 'TOGGLE_PROVIDER', provider: providerOrder[providerCursor]! });
      if (_input === 'a') dispatch({ type: 'TOGGLE_ALL' });
      if (_input === 'i') dispatch({ type: 'INVERT_SELECTION' });
      if (key.return) {
        if (state.selectedTypes.size === 0) {
          dispatch({ type: 'SET_ERROR', error: 'Please select at least one provider using <space> before pressing <enter>.' });
          return;
        }
        dispatch({ type: 'CONFIRM_PROVIDERS' });
        const firstType = [...state.selectedTypes][0];
        setFieldStage(firstType === 'azure' ? 'deploymentName' : firstType === 'openai-compatible' ? 'label' : firstType === 'ollama' ? 'baseUrl' : 'apiKey');
        setFieldBuffer('');
      }
    }
    if (state.step === 'TIER_ASSIGN') {
      // Remove enter and downArrow interception so SelectInput can use them
      if (key.tab || key.rightArrow) {
        const order: Array<'T1' | 'T2' | 'T3'> = ['T1', 'T2', 'T3'];
        const idx = order.indexOf(state.tierSelectFocus);
        dispatch({ type: 'SET_TIER_FOCUS', tier: order[(idx + 1) % 3]! });
      }
      // We will handle GO_SAVE within the onSelect of the final T3 tier or explicitly with a hotkey
    }
  });

  // ── Current entry being configured ──────────
  const currentEntry = state.entries[state.currentEntryIdx];

  // A "required" field (per the tag shown next to it) that's submitted blank
  // used to be silently accepted — the wizard would report success while
  // persisting an unusable provider entry (e.g. an empty apiKey, dropped on
  // save), which then correctly-but-confusingly re-triggered "needs setup" on
  // every subsequent `cascade` run. Reject it inline instead.
  const rejectIfBlank = useCallback((val: string, message: string): boolean => {
    if (val.trim()) return false;
    dispatch({ type: 'SET_ERROR', error: message });
    return true;
  }, []);

  const handleFieldSubmit = useCallback((val: string) => {
    if (!currentEntry) return;

    if (currentEntry.type === 'azure') {
      if (fieldStage === 'deploymentName') {
        if (rejectIfBlank(val, 'Deployment name is required.')) return;
        dispatch({ type: 'SET_ENTRY_FIELD', field: 'deploymentName', value: val });
        dispatch({ type: 'SET_ERROR', error: '' });
        setFieldBuffer('');
        if (currentEntry.baseUrl && currentEntry.apiKey && currentEntry.apiVersion) {
          setFieldStage('askMore');
        } else {
          setFieldStage('baseUrl');
        }
      } else if (fieldStage === 'baseUrl') {
        if (rejectIfBlank(val, 'Azure endpoint URL is required.')) return;
        dispatch({ type: 'SET_ENTRY_FIELD', field: 'baseUrl', value: val });
        dispatch({ type: 'SET_ERROR', error: '' });
        setFieldBuffer('');
        setFieldStage('apiKey');
      } else if (fieldStage === 'apiKey') {
        if (rejectIfBlank(val, 'API key is required.')) return;
        dispatch({ type: 'SET_ENTRY_FIELD', field: 'apiKey', value: val });
        dispatch({ type: 'SET_ERROR', error: '' });
        setFieldBuffer('');
        setFieldStage('apiVersion');
      } else if (fieldStage === 'apiVersion') {
        dispatch({ type: 'SET_ENTRY_FIELD', field: 'apiVersion', value: val || '2024-08-01-preview' });
        dispatch({ type: 'SET_ERROR', error: '' });
        setFieldBuffer('');
        setFieldStage('askMore');
      }
    } else if (currentEntry.type === 'openai-compatible') {
      if (fieldStage === 'label') {
        dispatch({ type: 'SET_ENTRY_FIELD', field: 'label', value: val || currentEntry.label });
        dispatch({ type: 'SET_ERROR', error: '' });
        setFieldBuffer('');
        setFieldStage('baseUrl');
      } else if (fieldStage === 'baseUrl') {
        if (rejectIfBlank(val, 'Base URL is required.')) return;
        dispatch({ type: 'SET_ENTRY_FIELD', field: 'baseUrl', value: val });
        dispatch({ type: 'SET_ERROR', error: '' });
        setFieldBuffer('');
        setFieldStage('apiKey');
      } else if (fieldStage === 'apiKey') {
        // Key is genuinely optional here (local servers need none) — no validation.
        dispatch({ type: 'SET_ENTRY_FIELD', field: 'apiKey', value: val });
        dispatch({ type: 'SET_ERROR', error: '' });
        setFieldBuffer('');
        setFieldStage('askMore');
      }
    } else if (currentEntry.type === 'ollama') {
      dispatch({ type: 'SET_ENTRY_FIELD', field: 'baseUrl', value: val || 'http://localhost:11434' });
      dispatch({ type: 'SET_ERROR', error: '' });
      setFieldBuffer('');
      setFieldStage('askMore');
    } else {
      // anthropic / openai / gemini — just need apiKey
      if (rejectIfBlank(val, 'API key is required.')) return;
      dispatch({ type: 'SET_ENTRY_FIELD', field: 'apiKey', value: val });
      dispatch({ type: 'SET_ERROR', error: '' });
      setFieldBuffer('');
      const nextEntry = state.entries[state.currentEntryIdx + 1];
      if (nextEntry) {
        setFieldStage(nextEntry.type === 'azure' ? 'deploymentName' : nextEntry.type === 'openai-compatible' ? 'label' : nextEntry.type === 'ollama' ? 'baseUrl' : 'apiKey');
      }
      dispatch({ type: 'NEXT_ENTRY' });
    }
  }, [currentEntry, fieldStage, rejectIfBlank, state.entries, state.currentEntryIdx]);

  // ── Render ────────────────────────────────────

  if (state.step === 'PROVIDER_SELECT') {
    return (
      <Frame theme={theme} phase="keys">
        <Box marginBottom={1}>
          <Text color={theme.colors.primary} bold>? </Text>
          <Text color={theme.colors.foreground} bold>Which providers do you want to configure?</Text>
        </Box>
        <Box flexDirection="column">
          {providerOrder.map((p, i) => {
            const selected = state.selectedTypes.has(p);
            const focused = i === providerCursor;
            return (
              <Box key={p}>
                <Text color={focused ? theme.colors.primary : theme.colors.muted}>{focused ? '❯ ' : '  '}</Text>
                <Text color={selected ? theme.colors.success : theme.colors.muted}>{selected ? '◉ ' : '◯ '}</Text>
                <Text color={focused ? theme.colors.primary : (selected ? theme.colors.foreground : theme.colors.muted)}>{PROVIDER_LABELS[p]}</Text>
                {p === 'azure' && <Text color={theme.colors.muted} dimColor>  — multiple deployments supported</Text>}
                {p === 'openai-compatible' && <Text color={theme.colors.muted} dimColor>  — Groq, Together, custom</Text>}
                {p === 'ollama' && <Text color={theme.colors.muted} dimColor>  — no API key needed</Text>}
              </Box>
            );
          })}
        </Box>
        <Box marginTop={1}>
          <Text color={theme.colors.muted} dimColor>space select  ·  a all  ·  i invert  ·  enter continue</Text>
        </Box>
        {state.error && <Text color={theme.colors.error}>{state.error}</Text>}
      </Frame>
    );
  }

  if (state.step === 'API_KEYS' && currentEntry) {
    const isAzure = currentEntry.type === 'azure';
    const isCompat = currentEntry.type === 'openai-compatible';
    const isOllama = currentEntry.type === 'ollama';
    const doneEntries = state.entries.slice(0, state.currentEntryIdx);

    if (fieldStage === 'askMore') {
      // After completing an Azure or compat entry, ask if they want another
      return (
        <Frame theme={theme} phase="keys">
          <Box marginBottom={1}>
            <Text color={theme.colors.primary} bold>? </Text>
            <Text color={theme.colors.foreground} bold>{isAzure ? 'Add another Azure deployment?' : isOllama ? 'Add another Ollama endpoint?' : 'Add another custom endpoint?'}</Text>
          </Box>
          <Box>
            <SelectInput
              items={[
                { label: 'Yes — add another', value: 'yes' },
                { label: 'No — continue', value: 'no' },
              ]}
              indicatorComponent={({ isSelected }) => <Text color={theme.colors.primary}>{isSelected ? '❯ ' : '  '}</Text>}
              itemComponent={({ isSelected, label }) => <Text color={isSelected ? theme.colors.primary : theme.colors.muted}>{label}</Text>}
              onSelect={(item) => {
                if (item.value === 'yes') {
                  if (isAzure) dispatch({ type: 'ADD_AZURE' });
                  else if (isOllama) dispatch({ type: 'ADD_OLLAMA' });
                  else dispatch({ type: 'ADD_COMPAT' });
                  setFieldStage(isAzure ? 'deploymentName' : isOllama ? 'baseUrl' : 'label');
                  setFieldBuffer('');
                } else {
                  const nextEntry = state.entries[state.currentEntryIdx + 1];
                  if (nextEntry) {
                    setFieldStage(nextEntry.type === 'azure' ? 'deploymentName' : nextEntry.type === 'openai-compatible' ? 'label' : nextEntry.type === 'ollama' ? 'baseUrl' : 'apiKey');
                  }
                  dispatch({ type: 'NEXT_ENTRY' });
                  setFieldBuffer('');
                }
              }}
            />
          </Box>
        </Frame>
      );
    }

    const prompt =
      isAzure && fieldStage === 'deploymentName' ? `Azure deployment name (${currentEntry.label})` :
      isAzure && fieldStage === 'baseUrl' ? `Azure endpoint URL` :
      isAzure && fieldStage === 'apiKey' ? `${currentEntry.label} API Key` :
      isAzure && fieldStage === 'apiVersion' ? `Azure API version (e.g. 2024-08-01-preview)` :
      isCompat && fieldStage === 'label' ? `Name for this endpoint (e.g. Groq)` :
      isCompat && fieldStage === 'baseUrl' ? `Base URL (e.g. https://api.groq.com/openai/v1)` :
      isCompat && fieldStage === 'apiKey' ? `${currentEntry.label} API Key (optional)` :
      isOllama ? `Ollama URL` :
      `${currentEntry.label} API Key`;

    // The OpenAI-compatible key is optional — local servers (llama.cpp, LM
    // Studio, vLLM without auth) need no key, so don't label it 'required'.
    const keyOptional = isCompat && fieldStage === 'apiKey';

    // apiVersion is not a secret — only mask the apiKey stage
    const isMasked = fieldStage === 'apiKey' && !isOllama;

    return (
      <Frame theme={theme} phase="keys">
        {doneEntries.length > 0 && (
          <Box flexDirection="column" marginBottom={1}>
            {doneEntries.map((e) => (
              <Box key={e.id}>
                <Text color={theme.colors.success}>✔ </Text>
                <Text color={theme.colors.muted}>{e.label}</Text>
              </Box>
            ))}
          </Box>
        )}
        <Box marginBottom={1}>
          <Text color={theme.colors.muted}>Provider {state.currentEntryIdx + 1} of {state.entries.length}</Text>
        </Box>
        <FieldBox
          theme={theme}
          label={prompt}
          tag={isOllama ? 'optional — Enter for default' : keyOptional ? 'optional — Enter to skip' : 'required'}
          tagColor={(isOllama || keyOptional) ? theme.colors.muted : theme.colors.error}
          active
        >
          <SafeTextInput
            value={fieldBuffer}
            onChange={setFieldBuffer}
            onSubmit={handleFieldSubmit}
            {...(isMasked ? { mask: '*' } : {})}
            placeholder={isOllama ? 'http://localhost:11434' : ''}
          />
        </FieldBox>
        {isMasked && (
          <Text color={theme.colors.muted} dimColor>
            Tip: Ctrl+V pastes from clipboard. Most terminals also support right-click paste.
          </Text>
        )}
        {state.error && <Text color={theme.colors.error}>{state.error}</Text>}
      </Frame>
    );
  }

  if (state.step === 'FETCH_MODELS') {
    return (
      <Frame theme={theme} phase="models">
        <Box marginBottom={1}>
          <Text color={theme.colors.primary} bold>? </Text>
          <Text color={theme.colors.foreground} bold>Connecting to providers and fetching models…</Text>
        </Box>
        <Box flexDirection="column">
          {state.fetchLog.map((line, i) => {
            const color = line.includes('✔')
              ? theme.colors.success
              : line.includes('✘')
                ? theme.colors.error
                : theme.colors.muted;
            return <Text key={i} color={color}>{line}</Text>;
          })}
          {state.fetchedModels.length === 0 && (
            <Box>
              <Text color={theme.colors.accent}><Spinner type="dots" /></Text>
            </Box>
          )}
        </Box>
      </Frame>
    );
  }

  if (state.step === 'TIER_ASSIGN') {
    const modelOptions = [
      { label: 'Auto — let Cascade choose best available', value: 'auto' },
      ...state.fetchedModels.map(m => ({
        label: `${m.name}  [${m.providerLabel}]`,
        value: m.id,
      })),
    ];

    const tierCard = (tier: 'T1' | 'T2' | 'T3', role: string, hint: string) => {
      const isFocused = state.tierSelectFocus === tier;
      const current = tier === 'T1' ? state.tierT1 : tier === 'T2' ? state.tierT2 : state.tierT3;
      const currentLabel = current === 'auto'
        ? 'Auto — let Cascade choose best available'
        : state.fetchedModels.find(m => m.id === current)?.name || current;

      if (!isFocused) {
        return (
          <TierCard key={tier} theme={theme} tier={tier} role={role} hint={hint}>
            <Box>
              <Text color={theme.colors.success}>✔ </Text>
              <Text color={theme.colors.foreground}>{currentLabel}</Text>
            </Box>
          </TierCard>
        );
      }

      return (
        <TierCard key={tier} theme={theme} tier={tier} role={role} hint={hint} active>
          <SelectInput
            items={modelOptions}
            limit={8}
            onSelect={(item) => {
              dispatch({ type: 'SET_TIER', tier, value: item.value });
              const order: Array<'T1' | 'T2' | 'T3'> = ['T1', 'T2', 'T3'];
              const idx = order.indexOf(tier);
              if (idx < 2) {
                dispatch({ type: 'SET_TIER_FOCUS', tier: order[idx + 1]! });
              } else {
                dispatch({ type: 'GO_SAVE' });
              }
            }}
            indicatorComponent={({ isSelected }) => <Text color={theme.colors.primary}>{isSelected ? '❯ ' : '  '}</Text>}
            itemComponent={({ isSelected, label }) => <Text color={isSelected ? theme.colors.primary : theme.colors.muted}>{label}</Text>}
          />
        </TierCard>
      );
    };

    return (
      <Frame theme={theme} phase="models">
        <Box marginBottom={1}>
          <Text color={theme.colors.primary} bold>? </Text>
          <Text color={theme.colors.foreground} bold>Assign a model to each tier</Text>
        </Box>
        <Box flexDirection="column">
          {tierCard('T1', 'Administrator', 'complex reasoning · once per task')}
          {tierCard('T2', 'Manager', 'per-section planning')}
          {tierCard('T3', 'Worker', 'high volume · many parallel runs')}
        </Box>
        <Box marginTop={1}>
          <Text color={theme.colors.muted} dimColor>Tab to skip a tier  ·  ↑↓ + Enter to choose</Text>
        </Box>
        {state.error && <Text color={theme.colors.error}>{state.error}</Text>}
      </Frame>
    );
  }

  if (state.step === 'SAVE') {
    return (
      <Frame theme={theme} phase="complete">
        <Box>
          <Text color={theme.colors.accent}><Spinner type="dots" /></Text>
          <Text color={theme.colors.foreground}> Writing .cascade/config.json…</Text>
        </Box>
        {state.error && <Text color={theme.colors.error}>Error: {state.error}</Text>}
      </Frame>
    );
  }

  if (state.step === 'DONE') {
    return (
      <Frame theme={theme} phase="complete">
        <Box flexDirection="column" alignItems="center">
          <Text color={theme.colors.primary} bold>◈</Text>
          <Text color={theme.colors.success} bold>Cascade AI initialized successfully!</Text>
          <Box marginTop={1}>
            <Text color={theme.colors.muted}>Run </Text>
            <Text color={theme.colors.primary} bold>cascade</Text>
            <Text color={theme.colors.muted}> inside any project directory to start.</Text>
          </Box>
          <Box marginTop={1} borderStyle="round" borderColor={theme.colors.border} paddingX={2}>
            <Text color={theme.colors.muted}>$ </Text>
            <Text color={theme.colors.accent} bold>cascade</Text>
          </Box>
          <Box marginTop={1}>
            <Text color={theme.colors.muted} dimColor>Press Enter to finish</Text>
          </Box>
        </Box>
      </Frame>
    );
  }

  return <Box />;
}

// ── Exported runner ───────────────────────────

export async function runSetupWizard(workspacePath: string): Promise<CascadeConfig> {
  // Prominent telemetry notice — shown on every first-run setup so users
  // understand the default is OFF and they can revisit the decision later.
  printTelemetryBanner();
  return new Promise((resolve) => {
    const { unmount } = render(
      React.createElement(SetupWizard, {
        workspacePath,
        onComplete: (config) => {
          unmount();
          resolve(config);
        },
      }),
      { exitOnCtrlC: true },
    );
  });
}

function printTelemetryBanner(): void {
  // Plain console output (outside Ink) so it survives re-mounts.
  // eslint-disable-next-line no-console
  console.log();
  // eslint-disable-next-line no-console
  console.log('  ┌──────────────────────────────────────────────────────────────┐');
  // eslint-disable-next-line no-console
  console.log('  │  Telemetry: OFF by default.                                  │');
  // eslint-disable-next-line no-console
  console.log('  │  Toggle anytime with:  cascade telemetry on | off | status   │');
  // eslint-disable-next-line no-console
  console.log('  │  Anonymous session metadata only — never prompts or output.  │');
  // eslint-disable-next-line no-console
  console.log('  └──────────────────────────────────────────────────────────────┘');
  // eslint-disable-next-line no-console
  console.log();
}
